/**
 * Push the Android shell's recent exit reasons to the server on boot.
 *
 * Before kiosk 0.2.49 a crash, an ANR, an out-of-memory kill or a WebView
 * renderer death reached us only if someone pressed "Capture logs". This
 * reads `FoyerNative.exitEvents()` once per page load and posts it to
 * `POST /screens/:id/exit-events`. The server keys each event, so posting the
 * same list on every boot is harmless and there is no local "sent" state to
 * lose when firmware wipes WebView storage.
 *
 * Each exit the page has not logged before also becomes a console line, so
 * it appears in the Device log with the lines around it.
 */

interface RawExit {
  source?: unknown;
  at?: unknown;
  reason?: unknown;
  description?: unknown;
  pid?: unknown;
}

const LOGGED_KEY = "foyer.nativeExits.lastLoggedAt";
const RETRY_DELAYS_MS = [5_000, 30_000, 120_000];

/** Parse what the shell returned. Anything malformed is an empty list. */
export function readNativeExits(): RawExit[] {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  if (typeof native?.exitEvents !== "function") return [];
  try {
    const raw = native.exitEvents();
    if (typeof raw !== "string" || !raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as RawExit[]).slice(0, 30) : [];
  } catch {
    return [];
  }
}

function logNewExits(events: RawExit[]): void {
  let last = 0;
  try {
    last = Number(window.localStorage.getItem(LOGGED_KEY)) || 0;
  } catch {
    /* storage refused: log them all, the server still dedupes */
  }
  let newest = last;
  for (const e of events) {
    const at = typeof e.at === "number" ? e.at : 0;
    if (at <= last) continue;
    newest = Math.max(newest, at);
    console.warn(
      `[native-exit] ${String(e.reason)} (${String(e.source)}) at ${new Date(at).toISOString()}` +
        (e.description ? ` - ${String(e.description)}` : "")
    );
  }
  if (newest > last) {
    try {
      window.localStorage.setItem(LOGGED_KEY, String(newest));
    } catch {
      /* best effort */
    }
  }
}

/**
 * Report once per page load, retrying a few times while the screen token is
 * not there yet or the network is down. Returns a cancel function.
 */
export function reportNativeExits(
  screenId: string,
  getScreenToken: () => string | undefined,
  fetchImpl: typeof fetch = (input, init) => fetch(input, init)
): () => void {
  const events = readNativeExits();
  if (events.length === 0) return () => {};
  logNewExits(events);

  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const attempt = async (i: number): Promise<void> => {
    if (cancelled) return;
    const token = getScreenToken();
    let ok = false;
    if (token) {
      try {
        const res = await fetchImpl(
          `/api/common/v1/screens/${screenId}/exit-events`,
          {
            method: "POST",
            headers: {
              "content-type": "application/json",
              "X-Screen-Token": token,
            },
            body: JSON.stringify({ events }),
          }
        );
        ok = res.ok;
      } catch {
        ok = false;
      }
    }
    if (!ok && !cancelled && i < RETRY_DELAYS_MS.length) {
      timer = setTimeout(() => void attempt(i + 1), RETRY_DELAYS_MS[i]);
    }
  };
  timer = setTimeout(() => void attempt(0), 0);

  return () => {
    cancelled = true;
    if (timer !== null) clearTimeout(timer);
  };
}
